import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import AuthenticatedImage from '../../../components/AuthenticatedImage';
import attendanceService from '../../../services/attendanceService';
import attendanceBatchService from '../../../services/attendanceBatchService';
import employeeService from '../../../services/employeeService';
import NotificationToast from '../../../components/ui/NotificationToast';
import DetailedAttendanceReport from './DetailedAttendanceReport';
import { formatTimeFromApi } from '../../../utils/attendanceDateTime';
import { monthDateRange, buildAttendancePhotoUrl, buildPunchPhotoUrl, resolveMediaUrl, getRecordPunchList } from '../../../utils/attendanceHelpers';

// Selfie Image Modal Component
const SelfieImageModal = ({ imageUrl, fallbackUrl, label, type }) => {
  const [showModal, setShowModal] = useState(false);
  const [thumbFailed, setThumbFailed] = useState(false);
  const [modalFailed, setModalFailed] = useState(false);

  const primaryUrl = imageUrl || fallbackUrl;

  if (!primaryUrl) return null;

  return (
    <>
      <button
        onClick={() => !thumbFailed && setShowModal(true)}
        className="relative group"
        title={`View ${label} Selfie`}
        disabled={thumbFailed}
      >
        <div className="w-10 h-10 rounded-lg overflow-hidden border-2 border-border hover:border-primary transition-colors">
          {!thumbFailed ? (
            <AuthenticatedImage
              src={primaryUrl}
              fallbackSrc={fallbackUrl && fallbackUrl !== primaryUrl ? fallbackUrl : null}
              alt={`${label} selfie`}
              className="w-full h-full object-cover"
              onLoadError={() => setThumbFailed(true)}
            />
          ) : (
            <div className="w-full h-full bg-muted flex items-center justify-center">
              <Icon name="Image" size={16} className="text-muted-foreground" />
            </div>
          )}
        </div>
        <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-primary border-2 border-card flex items-center justify-center">
          <Icon name={type === 'in' ? 'LogIn' : 'LogOut'} size={8} className="text-primary-foreground" />
        </div>
      </button>

      {showModal && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setShowModal(false)}
        >
          <div className="bg-card rounded-lg max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">{label} Selfie</h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            <div className="p-4">
              {!modalFailed ? (
              <AuthenticatedImage
                src={primaryUrl}
                fallbackSrc={fallbackUrl && fallbackUrl !== primaryUrl ? fallbackUrl : null}
                alt={`${label} selfie`}
                className="w-full h-auto rounded-lg"
                onLoadError={() => setModalFailed(true)}
              />
              ) : (
                <div className="w-full h-64 bg-muted flex items-center justify-center rounded-lg">
                  <div className="text-center">
                    <Icon name="ImageOff" size={48} className="text-muted-foreground mx-auto mb-2" />
                    <p className="text-muted-foreground">Image not available</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const PunchTimesCell = ({ punches, type, attendanceDate, formatTime }) => {
  const items = punches.filter((p) => p.type === type);
  if (!items.length) return <span className="text-muted-foreground">-</span>;

  return (
    <div className="space-y-1.5">
      {items.map((punch) => (
        <div key={punch.id} className="flex items-center gap-1.5 text-sm text-foreground">
          {items.length > 1 && (
            <span className="text-[10px] font-medium text-muted-foreground bg-muted px-1 rounded shrink-0">
              #{punch.seq}
            </span>
          )}
          <span className="whitespace-nowrap">{formatTime(punch.time, attendanceDate)}</span>
        </div>
      ))}
    </div>
  );
};

const PunchSelfiesCell = ({ punches, record }) => {
  const withPhoto = punches.filter((p) => p.photoUrl);
  if (!withPhoto.length) {
    const legacyIn = record.clock_in_photo_url;
    const legacyOut = record.clock_out_photo_url;
    if (!legacyIn && !legacyOut) return <span className="text-xs text-muted-foreground">-</span>;
    return (
      <div className="flex flex-wrap items-center gap-2">
        {legacyIn && (
          <SelfieImageModal
            imageUrl={resolveMediaUrl(record.clock_in_photo_url) || buildAttendancePhotoUrl(record.id, 'clock_in')}
            fallbackUrl={buildAttendancePhotoUrl(record.id, 'clock_in')}
            label="Clock In"
            type="in"
          />
        )}
        {legacyOut && (
          <SelfieImageModal
            imageUrl={resolveMediaUrl(record.clock_out_photo_url) || buildAttendancePhotoUrl(record.id, 'clock_out')}
            fallbackUrl={buildAttendancePhotoUrl(record.id, 'clock_out')}
            label="Clock Out"
            type="out"
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {withPhoto.map((punch) => {
        const isDbPunch = punch.id && !String(punch.id).includes('-');
        const apiUrl = isDbPunch ? buildPunchPhotoUrl(punch.id) : null;
        const resolvedUrl = resolveMediaUrl(punch.photoUrl);

        return (
          <SelfieImageModal
            key={punch.id}
            imageUrl={apiUrl || resolvedUrl}
            fallbackUrl={resolvedUrl && resolvedUrl !== apiUrl ? resolvedUrl : null}
            label={`${punch.type === 'IN' ? 'In' : 'Out'} #${punch.seq}`}
            type={punch.type === 'IN' ? 'in' : 'out'}
          />
        );
      })}
    </div>
  );
};

const PunchLocationsCell = ({ punches }) => {
  const withLocation = punches.filter((p) => p.location);
  if (!withLocation.length) return <span className="text-xs">-</span>;

  return (
    <div className="space-y-1.5 max-w-xs">
      {withLocation.map((punch) => (
        <div key={punch.id} className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground">
            {punch.type === 'IN' ? 'In' : 'Out'}
            {punch.seq > 1 ? ` #${punch.seq}` : ''}:
          </span>{' '}
          {punch.location}
          {punch.latitude && punch.longitude && (
            <a
              href={`https://www.google.com/maps?q=${punch.latitude},${punch.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-1 text-primary hover:underline inline-flex items-center align-middle"
              title="View on Map"
            >
              <Icon name="MapPin" size={12} />
            </a>
          )}
        </div>
      ))}
    </div>
  );
};

const AttendanceReports = ({ isAdmin, clientId }) => {
  const [activeTab, setActiveTab] = useState('list');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [filters, setFilters] = useState(() => {
    const { start, end } = monthDateRange();
    return {
      employee_id: '',
      date_from: start,
      date_to: end,
      status: '',
    };
  });
  const [notification, setNotification] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchUpdates, setBatchUpdates] = useState({
    status: '',
    notes: ''
  });

  useEffect(() => {
    loadEmployees();
    loadRecords();
  }, [clientId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadRecords();
    }, 500);
    return () => clearTimeout(timer);
  }, [filters, clientId]);

  const loadEmployees = async () => {
    try {
      const result = await employeeService.getEmployees();
      if (result?.success) {
        setEmployees(result.data || []);
      }
    } catch (error) {
      console.log('Error loading employees:', error);
    }
  };

  const loadRecords = async () => {
    try {
      setLoading(true);
      const requestFilters = { ...filters };
      if (clientId) {
        requestFilters.client_id = clientId;
      }
      const result = await attendanceService.getAttendanceRecords(requestFilters);
      if (result?.success) {
        setRecords(result.data || []);
      }
    } catch (error) {
      console.log('Error loading records:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleExport = async (format = 'csv') => {
    try {
      const result = await attendanceService.exportAttendance(filters, format);
      if (result?.success) {
        setNotification({ 
          message: `Attendance data exported successfully as ${format.toUpperCase()}`, 
          type: 'success' 
        });
      } else {
        setNotification({ message: result?.error || 'Failed to export data', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred during export', type: 'error' });
      console.log('Error exporting:', error);
    }
  };

  const handleBatchExport = async () => {
    if (selectedRecords.length === 0) {
      setNotification({ message: 'Please select records to export', type: 'error' });
      return;
    }

    try {
      const result = await attendanceBatchService.batchExport(selectedRecords, 'csv');
      if (result?.success) {
        setNotification({ 
          message: `Exported ${selectedRecords.length} record(s) successfully`, 
          type: 'success' 
        });
        setSelectedRecords([]);
      } else {
        setNotification({ message: result?.error || 'Failed to export', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred during export', type: 'error' });
      console.log('Error batch exporting:', error);
    }
  };

  const handleBatchUpdate = async () => {
    if (selectedRecords.length === 0) {
      setNotification({ message: 'Please select records to update', type: 'error' });
      return;
    }

    try {
      const updates = {};
      if (batchUpdates.status) updates.status = batchUpdates.status;
      if (batchUpdates.notes) updates.notes = batchUpdates.notes;

      if (Object.keys(updates).length === 0) {
        setNotification({ message: 'Please provide at least one update', type: 'error' });
        return;
      }

      const result = await attendanceBatchService.batchUpdate(selectedRecords, updates);
      if (result?.success) {
        setNotification({ 
          message: `Updated ${result.data.success_count} record(s) successfully`, 
          type: 'success' 
        });
        setShowBatchModal(false);
        setSelectedRecords([]);
        setBatchUpdates({ status: '', notes: '' });
        loadRecords();
      } else {
        setNotification({ message: result?.error || 'Failed to update records', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred during batch update', type: 'error' });
      console.log('Error batch updating:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'present': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'late': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300';
      case 'absent': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      case 'early_departure': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
      case 'half_day': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-6">
      <NotificationToast
        message={notification?.message}
        type={notification?.type}
        onDismiss={() => setNotification(null)}
      />

      <div className="flex items-center justify-between mb-6">
        <div>
          <h4 className="text-lg font-semibold text-foreground">Attendance Reports</h4>
          <p className="text-sm text-muted-foreground">
            View and export attendance records (same data as Employee Portal when employee and month match)
          </p>
        </div>
        {activeTab === 'list' && (
          <div className="flex gap-2">
            {isAdmin && selectedRecords.length > 0 && (
              <>
                <Button 
                  onClick={() => setShowBatchModal(true)} 
                  variant="outline" 
                  className="flex items-center gap-2"
                >
                  <Icon name="Edit" size={16} />
                  Batch Edit ({selectedRecords.length})
                </Button>
                <Button 
                  onClick={() => handleBatchExport()} 
                  variant="outline" 
                  className="flex items-center gap-2"
                >
                  <Icon name="Download" size={16} />
                  Export Selected
                </Button>
              </>
            )}
            <Button onClick={() => handleExport('csv')} variant="outline" className="flex items-center gap-2">
              <Icon name="Download" size={16} />
              Export CSV
            </Button>
            <Button onClick={() => handleExport('excel')} className="flex items-center gap-2">
              <Icon name="FileSpreadsheet" size={16} />
              Export Excel
            </Button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-border">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab('list')}
            className={`pb-3 px-1 border-b-2 transition-colors ${
              activeTab === 'list'
                ? 'border-primary text-primary font-medium'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            List View
          </button>
          <button
            onClick={() => setActiveTab('detailed')}
            className={`pb-3 px-1 border-b-2 transition-colors ${
              activeTab === 'detailed'
                ? 'border-primary text-primary font-medium'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Detailed Report (A4 Landscape)
          </button>
        </div>
      </div>

      {activeTab === 'detailed' ? (
        <DetailedAttendanceReport isAdmin={isAdmin} />
      ) : (
        <>

      {/* Filters */}
      <div className="mb-6 p-4 bg-muted/50 rounded-lg border border-border">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {isAdmin && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Employee</label>
              <Select
                value={filters.employee_id}
                onValueChange={(value) => handleFilterChange('employee_id', value)}
                placeholder="All Employees"
                options={[
                  { value: '', label: 'All Employees' },
                  ...(employees.map(emp => ({
                    value: emp.id,
                    label: `${emp.full_name} (${emp.employee_id})`
                  })) || [])
                ]}
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Date From</label>
            <Input
              type="date"
              value={filters.date_from}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Date To</label>
            <Input
              type="date"
              value={filters.date_to}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Status</label>
            <Select
              value={filters.status}
              onValueChange={(value) => handleFilterChange('status', value)}
              placeholder="All Statuses"
              options={[
                { value: '', label: 'All Statuses' },
                { value: 'present', label: 'Present' },
                { value: 'late', label: 'Late' },
                { value: 'absent', label: 'Absent' },
                { value: 'early_departure', label: 'Early Departure' },
                { value: 'half_day', label: 'Half Day' }
              ]}
            />
          </div>
        </div>
      </div>

      {/* Records Table */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading records...</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                {isAdmin && (
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedRecords.length === records.length && records.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedRecords(records.map(r => r.id));
                        } else {
                          setSelectedRecords([]);
                        }
                      }}
                      className="rounded border-border"
                    />
                  </th>
                )}
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Employee</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Clock In</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Clock Out</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Hours</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Selfie</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Location</th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {records.length === 0 ? (
                <tr>
                  <td colSpan={isAdmin ? 10 : 9} className="px-4 py-8 text-center text-muted-foreground">
                    No attendance records found
                  </td>
                </tr>
              ) : (
                records.map(record => {
                  const punches = getRecordPunchList(record);
                  const sessionCount = punches.filter((p) => p.type === 'IN').length;

                  return (
                  <tr key={record.id} className="hover:bg-muted/50">
                    {isAdmin && (
                      <td className="px-4 py-3 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedRecords.includes(record.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedRecords([...selectedRecords, record.id]);
                            } else {
                              setSelectedRecords(selectedRecords.filter(id => id !== record.id));
                            }
                          }}
                          className="rounded border-border"
                        />
                      </td>
                    )}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-foreground">
                      {new Date(record.attendance_date).toLocaleDateString()}
                      {sessionCount > 1 && (
                        <span className="block text-[10px] text-muted-foreground mt-0.5">
                          {sessionCount} sessions
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-foreground">
                      {record.full_name || 'N/A'}
                      {record.employee_id && (
                        <span className="text-xs text-muted-foreground block">{record.employee_id}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground align-top">
                      <PunchTimesCell
                        punches={punches}
                        type="IN"
                        attendanceDate={record.attendance_date}
                        formatTime={formatTimeFromApi}
                      />
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground align-top">
                      <PunchTimesCell
                        punches={punches}
                        type="OUT"
                        attendanceDate={record.attendance_date}
                        formatTime={formatTimeFromApi}
                      />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-foreground">
                      {record.work_hours ? `${record.work_hours}h` : '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(record.status)}`}>
                        {record.status?.replace('_', ' ').toUpperCase() || 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <PunchSelfiesCell punches={punches} record={record} />
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground align-top">
                      <PunchLocationsCell punches={punches} />
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Batch Edit Modal */}
      {showBatchModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-lg border border-border w-full max-w-md">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-foreground">Batch Edit Attendance</h3>
                <Button variant="ghost" size="icon" onClick={() => setShowBatchModal(false)}>
                  <Icon name="X" size={20} />
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Updating {selectedRecords.length} record(s)
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Status</label>
                <Select
                  value={batchUpdates.status}
                  onValueChange={(value) => setBatchUpdates(prev => ({ ...prev, status: value }))}
                  placeholder="Select status (optional)"
                  options={[
                    { value: '', label: 'No change' },
                    { value: 'present', label: 'Present' },
                    { value: 'late', label: 'Late' },
                    { value: 'absent', label: 'Absent' },
                    { value: 'half_day', label: 'Half Day' },
                    { value: 'early_departure', label: 'Early Departure' }
                  ]}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Notes</label>
                <textarea
                  value={batchUpdates.notes}
                  onChange={(e) => setBatchUpdates(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                  placeholder="Add notes (optional)"
                />
              </div>
            </div>
            <div className="p-6 border-t border-border flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowBatchModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleBatchUpdate}>
                Update {selectedRecords.length} Record(s)
              </Button>
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default AttendanceReports;

